import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { attach } from "../../common/componentExtensions";
import { GetTable } from "../../common/components/DataTable";
import DatesHelper from "../../common/datesHelper";
import Period from "../../common/period";
import PeriodInput from "../../common/components/periodInput";
import { CustomerData } from "../../customers/components/CustomerData";
import { ImpiantoAddressDescription, ImpiantoLightDataComponent } from "../../impianti/components/impiantoLightData";
import { InterventoDto } from "../../generated";
import InterventiActions from "../../interventi/InterventiActions";
import InterventiReportStore from "../../interventi/InterventiReportStore";
import { links } from "../../routes";
import { Link } from "../../common/components/link";
import CatastiActions, { ValidationError } from "../CatastiActions";

const Table = GetTable<InterventoDto>();

interface Props extends RouteComponentProps {
    period: Period;
    interventi: InterventoDto[];
}

interface State {
    selectedInterventi: InterventoDto[];
    siertErrors: ValidationError[] | null;
    isExporting: boolean;
    showSiertModal: boolean;
    siertInputValue: string;
}

const CatastiPage: React.FunctionComponent<Props> = ({ period, interventi, location }) => {
    const history = useHistory();
    const [state, setState] = React.useState<State>({
        selectedInterventi: [],
        siertErrors: null,
        isExporting: false,
        showSiertModal: false,
        siertInputValue: "",
    });

    // Fetch interventi when period changes
    React.useEffect(() => {
        const stored = InterventiReportStore.getForPeriod(period);
        if (!Array.isArray(stored)) {
            InterventiActions.getReportInterventi(period.from, period.to);
        }
    }, [period.from.valueOf(), period.to.valueOf()]);

    // Auto-select all exportable interventions when data loads or period changes
    React.useEffect(() => {
        setState(s => ({
            ...s,
            selectedInterventi: interventi.filter(i => i.rapportoControlloId != null),
            siertErrors: null,
        }));
    }, [period.from.valueOf(), period.to.valueOf(), interventi.length]);

    const exportableCount = interventi.filter(i => i.rapportoControlloId != null).length;

    const handleSiertClick = () => {
        const saved = CatastiActions.getSavedIdAziendaSiert();
        setState(s => ({
            ...s,
            showSiertModal: true,
            siertInputValue: saved > 0 ? saved.toString() : "",
        }));
    };

    const handleSiertConfirm = async () => {
        const id = parseInt(state.siertInputValue);
        if (!id || id <= 0) return;
        CatastiActions.saveIdAziendaSiert(id);
        const ids = state.selectedInterventi.map(x => x.idIntervento);
        setState(s => ({ ...s, isExporting: true, siertErrors: null, showSiertModal: false }));
        try {
            // Step 1: verifica errori di validazione
            const errors = await CatastiActions.checkSiert(ids, id);
            if (errors.length > 0) {
                setState(s => ({ ...s, isExporting: false, siertErrors: errors }));
                return;
            }
            // Step 2: nessun errore → scarica il file
            await CatastiActions.downloadSiert(ids, id);
            setState(s => ({ ...s, isExporting: false, siertErrors: [] }));
        } catch {
            setState(s => ({ ...s, isExporting: false }));
        }
    };

    // Raggruppa gli errori per targa impianto
    const errorsByImpianto = state.siertErrors
        ? state.siertErrors.reduce<Record<string, ValidationError[]>>((acc, err) => {
            const key = err.entita || "(senza targa)";
            if (!acc[key]) acc[key] = [];
            acc[key].push(err);
            return acc;
        }, {})
        : null;

    const hasSelection = state.selectedInterventi.length > 0;

    return (
        <div className="card">
            <div className="card-header bg-theme text-white">
                <h3 className="text-white mb-0">Export Catasti</h3>
            </div>
            <div className="card-body">

                {/* Filtri periodo */}
                <div className="row mb-3">
                    <div className="col-12 col-md-6">
                        <PeriodInput
                            labelFrom="Interventi dal"
                            labelTo="Interventi al"
                            period={period}
                            onChange={p => {
                                history.push({
                                    pathname: location.pathname,
                                    search: "?" + new URLSearchParams({
                                        from: DatesHelper.compactDateString(p.from),
                                        to: DatesHelper.compactDateString(p.to),
                                    }).toString()
                                });
                            }}
                        />
                    </div>
                </div>

                {/* Riepilogo */}
                <div className="mb-2 text-muted" style={{ fontSize: "0.9em" }}>
                    <i className="fas fa-info-circle me-1" />
                    {interventi.length === 0
                        ? "Nessun intervento nel periodo selezionato."
                        : <>
                            <strong>{exportableCount}</strong> di <strong>{interventi.length}</strong> interventi
                            nel periodo hanno un rapporto di controllo e possono essere esportati.
                            {exportableCount === 0 && <span className="text-warning ms-2">
                                <i className="fas fa-exclamation-triangle me-1" />
                                Nessun intervento esportabile.
                            </span>}
                        </>
                    }
                </div>

                {/* Tabella interventi */}
                <Table
                    keySelector={x => x.idIntervento.toString()}
                    filterables={[
                        x => x.dettaglioIntervento,
                        x => x.oggettoIntervento,
                        x => DatesHelper.getReadableDateString(x.dataIntervento),
                    ]}
                    data={interventi}
                >
                    <Table.SelectColumn
                        selectedItems={state.selectedInterventi}
                        onSelectionChanged={selectedInterventi => setState(s => ({ ...s, selectedInterventi, siertErrors: null }))}
                        disabled={x => x.rapportoControlloId == null}
                    />
                    <Table.Column
                        header="Data"
                        orderable
                        template={i => <span style={{ whiteSpace: "nowrap" }}>
                            {DatesHelper.getReadableDateString(i.dataIntervento)}
                        </span>}
                        orderBy={x => x.dataIntervento.valueOf()}
                        groupBy={x => DatesHelper.getReadableDateString(x.dataIntervento)}
                        defaultGroupingIndex={0}
                    />
                    <Table.Column
                        header="Cliente"
                        template={i => i.idCliente
                            ? <CustomerData id={i.idCliente} template={c => (
                                <Link to={links.customerShow(c.idCliente)}>
                                    {c.cognomeProprietario} {c.nomeProprietario}
                                    {c.responsabile && c.responsabile !== `${c.cognomeProprietario} ${c.nomeProprietario}`
                                        && <div className="text-muted" style={{ fontSize: "0.85em" }}>{c.responsabile}</div>}
                                </Link>
                            )} />
                            : <></>
                        }
                    />
                    <Table.Column
                        header="Indirizzo impianto"
                        groupable={false}
                        template={i => i.idCliente
                            ? <CustomerData id={i.idCliente} template={c =>
                                i.idImpianto
                                    ? <ImpiantoAddressDescription customer={c} idImpianto={i.idImpianto} />
                                    : <></>
                            } />
                            : <></>
                        }
                    />
                    <Table.Column
                        header="Impianto"
                        orderable groupable
                        template={i => i.idImpianto && i.idCliente
                            ? <ImpiantoLightDataComponent
                                id={i.idImpianto}
                                idCliente={i.idCliente}
                                template={imp => <span>{imp.marca} {imp.modello}</span>}
                            />
                            : <></>
                        }
                    />
                    <Table.Column
                        header="Rapporto"
                        template={i => i.rapportoControlloId != null
                            ? <span className="badge bg-success"><i className="fas fa-check me-1" />Presente</span>
                            : <span className="badge bg-secondary text-white"><i className="fas fa-times me-1" />Assente</span>
                        }
                    />
                </Table>

                {/* Barra azioni sticky (desktop) */}
                {hasSelection && (
                    <div className="d-none d-md-block w-100 card sticky-top mt-3" style={{ bottom: 10, zIndex: 2 }}>
                        <div className="card-body d-flex align-items-center flex-wrap gap-2">
                            <span className="me-2 text-muted">
                                <strong>{state.selectedInterventi.length}</strong> interventi selezionati
                            </span>
                            {state.isExporting ? (
                                <button className="btn btn-primary" disabled>
                                    <i className="fas fa-spinner fa-spin me-2" />Esportazione...
                                </button>
                            ) : (
                                <button className="btn btn-primary" onClick={handleSiertClick}>
                                    <i className="fas fa-file-export me-2" />Export SIERT
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Barra azioni sticky (mobile) */}
                {hasSelection && (
                    <div className="d-flex d-md-none w-100 card sticky-bottom mt-2" style={{ zIndex: 2 }}>
                        <div className="card-footer d-flex justify-content-around gap-2">
                            {state.isExporting ? (
                                <button className="btn btn-primary d-flex flex-column align-items-center justify-content-center" disabled style={{ minWidth: 70 }}>
                                    <i className="fas fa-spinner fa-spin" />
                                    <span style={{ fontSize: "0.65em" }}>...</span>
                                </button>
                            ) : (
                                <button
                                    className="btn btn-primary d-flex flex-column align-items-center justify-content-center"
                                    onClick={handleSiertClick}
                                    style={{ minWidth: 70 }}
                                >
                                    <i className="fas fa-file-export" />
                                    <span style={{ fontSize: "0.65em" }}>SIERT</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Modal ID Azienda SIERT */}
                {state.showSiertModal && (
                    <>
                        <div className="modal fade show d-block" tabIndex={-1} onClick={() => setState(s => ({ ...s, showSiertModal: false }))}>
                            <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            <i className="fas fa-file-export me-2" />Export SIERT
                                        </h5>
                                        <button type="button" className="btn-close" onClick={() => setState(s => ({ ...s, showSiertModal: false }))} />
                                    </div>
                                    <div className="modal-body">
                                        <p className="text-muted mb-3" style={{ fontSize: "0.9em" }}>
                                            Verranno esportati <strong>{state.selectedInterventi.length}</strong> interventi
                                            con rapporto di controllo.
                                        </p>
                                        <label className="form-label fw-semibold">ID Azienda SIERT</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="es. 12345"
                                            value={state.siertInputValue}
                                            min={1}
                                            autoFocus
                                            onChange={e => setState(s => ({ ...s, siertInputValue: e.target.value }))}
                                            onKeyDown={e => {
                                                if (e.key === "Enter") handleSiertConfirm();
                                                if (e.key === "Escape") setState(s => ({ ...s, showSiertModal: false }));
                                            }}
                                        />
                                        <div className="form-text">
                                            Il codice viene salvato automaticamente per le esportazioni successive.
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setState(s => ({ ...s, showSiertModal: false }))}
                                        >
                                            Annulla
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={!state.siertInputValue || parseInt(state.siertInputValue) <= 0}
                                            onClick={handleSiertConfirm}
                                        >
                                            <i className="fas fa-download me-2" />Esporta
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-backdrop fade show" />
                    </>
                )}

                {/* Pannello errori SIERT */}
                {state.siertErrors !== null && (
                    <div className="mt-4">
                        {state.siertErrors.length === 0
                            ? (
                                <div className="alert alert-success d-flex align-items-center">
                                    <i className="fas fa-check-circle me-2 fs-5" />
                                    Export completato con successo.
                                </div>
                            )
                            : (
                                <div className="alert alert-danger p-0 border-danger">
                                    <div className="d-flex align-items-center px-3 py-2 border-bottom border-danger">
                                        <i className="fas fa-exclamation-triangle me-2 fs-5" />
                                        <strong>
                                            Export SIERT non riuscito — {state.siertErrors.length} errore/i da correggere
                                        </strong>
                                        <button
                                            type="button"
                                            className="btn-close ms-auto"
                                            onClick={() => setState(s => ({ ...s, siertErrors: null }))}
                                        />
                                    </div>

                                    {/* Errori raggruppati per impianto */}
                                    {errorsByImpianto && Object.entries(errorsByImpianto).map(([targa, errori]) => (
                                        <div key={targa} className="px-3 py-2 border-bottom border-danger border-opacity-25">
                                            <div className="fw-semibold mb-1">
                                                <i className="fas fa-home me-1" />
                                                Impianto: <span className="font-monospace">{targa}</span>
                                                <span className="badge bg-danger ms-2">{errori.length}</span>
                                            </div>
                                            <table className="table table-sm table-borderless mb-0" style={{ fontSize: "0.88em" }}>
                                                <thead className="text-muted">
                                                    <tr>
                                                        <th style={{ width: "28%" }}>Campo</th>
                                                        <th style={{ width: "50%" }}>Errore</th>
                                                        <th style={{ width: "22%" }}>Valore ricevuto</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {errori.map((err, i) => (
                                                        <tr key={i}>
                                                            <td className="text-nowrap text-muted">{err.campo}</td>
                                                            <td>{err.messaggio}</td>
                                                            <td>
                                                                <code className="text-danger">
                                                                    {err.valore !== "" ? err.valore : <em className="text-muted">vuoto</em>}
                                                                </code>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ))}
                                </div>
                            )
                        }
                    </div>
                )}
            </div>
        </div>
    );
};

export default attach(CatastiPage, InterventiReportStore, (store, props) => {
    const params = new URLSearchParams(props.location.search);
    const today = DatesHelper.Today();
    const from = params.get("from");
    const to = params.get("to");
    const fromDate = from ? DatesHelper.fromCompactDateString(from) : today;
    const toDate = to ? DatesHelper.fromCompactDateString(to) : DatesHelper.AddDays(today, 30);
    const period = Period.Create(fromDate, toDate);
    const stored = store.getForPeriod(period);
    return {
        period,
        interventi: Array.isArray(stored) ? stored : [],
    };
});
